/*
 * `Icon` — the one way the interface draws a glyph: no icon font, no network request, and a name
 * that is not in the set is a compile error at the call site.
 *
 * The set is lucide's, taken as drawings rather than as a dependency: `icons.ts` maps every teamBase
 * name to a lucide one, `icons.gen.ts` holds that drawing as a single `d` string, and nothing is
 * loaded at runtime. One `<path>` per icon on the same 24 grid means one stroke weight, one cap and
 * one join across the whole interface — the reason the set is generated and not drawn by hand.
 *
 * Every icon is decorative: the control around it carries the accessible name, so each one is
 * `aria-hidden`. The one exception to stroke-only drawing is a name ending in `Filled`, whose path
 * is painted because the fill *is* the state it reports (a starred conversation against an
 * unstarred one); `FILLED` below is the closed list of those, and lucide's `star` is a single closed
 * path, so the paint stays inside the outline.
 */

import { PATHS } from "./icons.gen.ts"
import { ICONS, type IconName } from "./icons.ts"

export type { IconName }

/** The names whose path is painted, not stroked; see the note at the top of the file. */
const FILLED: ReadonlySet<IconName> = new Set<IconName>(["starFilled"])

/**
 * `size` is the drawn box in CSS pixels. Twenty is what a rail item, a menu row and a toolbar button
 * want; the callers that ask for something else are the 14-px hints inside a sidebar row and the
 * 16-px marks in front of a menu label, where 20 would out-shout the text beside it.
 */
export function Icon({ name, size = 20 }: { name: IconName; size?: number }) {
	return (
		<svg
			className="tb-icon"
			viewBox="0 0 24 24"
			width={size}
			height={size}
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			focusable="false"
		>
			<path d={PATHS[ICONS[name]]} fill={FILLED.has(name) ? "currentColor" : undefined} />
		</svg>
	)
}
